import { buildMessageSearchText } from "../message/mcp-chat-tool-transcript";
import type { ChatTab, SearchMatch } from "../shared/mcp-chat-types";

const SNIPPET_RADIUS = 30;

/**
 * Search messages across one or all tabs. Returns matches in chronological order,
 * active tab first when cross-tab is enabled.
 */
export function searchMessages(query: string, tabs: ChatTab[], activeTabId: string, crossTab: boolean): SearchMatch[] {
  if (!query || query.length < 2) return [];

  const lowerQuery = query.toLowerCase();
  const matches: SearchMatch[] = [];

  const tabsToSearch = crossTab
    ? [tabs.find((t) => t.id === activeTabId)!, ...tabs.filter((t) => t.id !== activeTabId)].filter(Boolean)
    : tabs.filter((t) => t.id === activeTabId);

  for (const tab of tabsToSearch) {
    for (const msg of tab.messages) {
      if (msg.role === "tool_status") continue;

      const content = buildMessageSearchText(msg);
      const lowerContent = content.toLowerCase();
      let searchFrom = 0;

      while (searchFrom < lowerContent.length) {
        const idx = lowerContent.indexOf(lowerQuery, searchFrom);
        if (idx === -1) break;

        matches.push({
          messageId: msg.id,
          tabId: tab.id,
          tabName: tab.name,
          startIndex: idx,
          snippet: buildSnippet(content, idx, query.length)
        });

        searchFrom = idx + lowerQuery.length;
      }
    }
  }

  return matches;
}

function buildSnippet(content: string, matchIndex: number, matchLength: number): string {
  const start = Math.max(0, matchIndex - SNIPPET_RADIUS);
  const end = Math.min(content.length, matchIndex + matchLength + SNIPPET_RADIUS);

  let snippet = content.slice(start, end).replace(/\n/g, " ");
  if (start > 0) snippet = "..." + snippet;
  if (end < content.length) snippet = snippet + "...";

  return snippet;
}

/**
 * Split text into segments, marking which ones match the query.
 * Used by the message renderer to wrap matches in <mark>.
 */
export function highlightText(text: string, query: string): Array<{ text: string; isMatch: boolean }> {
  if (!query || query.length < 2) return [{ text, isMatch: false }];

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const segments: Array<{ text: string; isMatch: boolean }> = [];
  let lastIndex = 0;

  while (lastIndex < lowerText.length) {
    const idx = lowerText.indexOf(lowerQuery, lastIndex);
    if (idx === -1) break;

    if (idx > lastIndex) {
      segments.push({ text: text.slice(lastIndex, idx), isMatch: false });
    }
    segments.push({ text: text.slice(idx, idx + query.length), isMatch: true });
    lastIndex = idx + query.length;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), isMatch: false });
  }

  return segments.length > 0 ? segments : [{ text, isMatch: false }];
}

/**
 * Group matches by tabId and return counts.
 */
export function getMatchCountByTab(matches: SearchMatch[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const m of matches) {
    counts.set(m.tabId, (counts.get(m.tabId) || 0) + 1);
  }
  return counts;
}
